import React, { useCallback, useEffect, useState } from 'react';
import { Box } from '@mui/material';
import { ErrorOutline } from '@mui/icons-material';
import Tooltip from '@mui/material/Tooltip';
import type { HydratedMediaItem, MediaStatusResult } from './media-type-config';
import getAttachmentURL, { getDownloadURL } from '../../api/attachments-actions/attachments-info';

interface AttachmentItemProps {
  item: HydratedMediaItem;
  statusChecker?: (mediaID: string) => Promise<MediaStatusResult>;
  editing?: boolean;
  attachmentsUrl?: string;
  token?: string;
}

const warningTooltipCopy = 'File hasn\'t been synced correctly and can not be downloaded. Please upload your file again.';

/**
 * Renders a single attachment item: filename text (or download link in
 * view mode) with an optional warning icon when the attachment status
 * indicates an issue.
 */
const AttachmentItem: React.FC<AttachmentItemProps> = ({
  item, statusChecker, editing, attachmentsUrl, token,
}) => {
  const [showWarning, setShowWarning] = useState(false);

  // Check the server-side processing status of the attachment (e.g. virus scan,
  // upload completion). Shows a warning icon when the status is not terminal.
  useEffect(() => {
    let cancelled = false;

    if (statusChecker) {
      statusChecker(item.mediaID)
        .then((result) => {
          if (!cancelled) setShowWarning(!result.ok);
        })
        .catch(() => {
          if (!cancelled) setShowWarning(true);
        });
    }

    return () => { cancelled = true; };
  }, [item.mediaID, statusChecker]);

  const warningIcon = showWarning
    ? (
      <Tooltip title={warningTooltipCopy} placement="top">
        <ErrorOutline
          data-testid="attachment-warning-icon"
          color="action"
          sx={{ fontSize: '20px', ml: '4px', mb: '-5px' }}
        />
      </Tooltip>
    )
    : null;

  const downloadUrl = attachmentsUrl ? getDownloadURL(item.mediaID, attachmentsUrl) : '';

  const handleDownloadClick = useCallback(async (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (!downloadUrl || !token) {
      // Allow the browser to handle navigation when we cannot perform an authenticated download.
      return;
    }

    event.preventDefault();

    try {
      const response = await getAttachmentURL(downloadUrl, token);

      if (!response.ok) return;

      globalThis.location.href = response.url;
    } catch {
      // Degrade gracefully — the href fallback is still present on the element
    }
  }, [downloadUrl, token]);

  if (!editing) {
    return (
      <Box sx={{ maxWidth: 400 }}>
        {showWarning
          ? <span>{item.name}</span>
          : (
            <a
              href={downloadUrl}
              title={item.name}
              download={item.name}
              target="_blank"
              rel="noreferrer"
              onClick={handleDownloadClick}
            >
              <span>{item.name}</span>
            </a>
          )}
        {warningIcon}
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 400, ml: '30px' }}>
      <span>{item.name}</span>
      {warningIcon}
    </Box>
  );
};

export default AttachmentItem;
